import { Alert } from '@creditornot/cb-alert';
import { CaretRight, InfoCircle } from '@creditornot/cb-icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import React, { Fragment, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { typographyCss } from 'common/typography';
import Loading from 'core/Loading';
import { DiscountedItem, DiscountedItemCategory } from 'modules/campaigns/hooks/useDiscountedItems';

import { SelectedDiscountItem } from './SelectedDiscountItem';
import { SelectedDiscountedItemsModal } from './SelectedDiscountedItemsModal';
import { getSelectionByCategory, getVisibleCategoriesWithItems } from './utils';

type DiscountedItemsGridProps = {
  selectedItemIds: string[];
  selectedCategoryIds: string[];
  discountedItems: DiscountedItem[];
  discountedItemCategories: DiscountedItemCategory[];
  isLoadingMenuData: boolean;
  onEditClick?: () => void;
};

const Category = styled.div``;

const SelectedItemsGrid = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-block: 8px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryTitle = styled.h3`
  ${typographyCss.Body3}
  font-weight: 500;
  margin-block: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0 4px;
  overflow: hidden;
`;

const CategoryName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryNameCaret = styled(CaretRight)`
  width: 8px;
  fill: ${color.text};

  ${({ theme }) =>
    theme.cbDirection === 'rtl' &&
    css`
      transform: rotate(180deg);
    `}
`;

const CategoryInfo = styled.p`
  ${typographyCss.Small}
  margin-block: 2px 0;
  color: ${color.textSubdued};
  display: flex;
  gap: 4px;
`;

const InfoIcon = styled(InfoCircle)`
  width: 12px;
  height: 12px;
  fill: ${color.textSubdued};
  margin-block: 2px;
`;

const Emphasized = styled.span`
  font-weight: 700;
`;

const MAX_VISIBLE_ITEM_COUNT = 16;
const MAX_VISIBLE_ITEM_COUNT_MOBILE = 8;

export const DiscountedItemsGrid = ({
  selectedItemIds,
  selectedCategoryIds,
  discountedItems,
  discountedItemCategories,
  isLoadingMenuData,
  onEditClick,
}: DiscountedItemsGridProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const maxItemsCount = isMobile ? MAX_VISIBLE_ITEM_COUNT_MOBILE : MAX_VISIBLE_ITEM_COUNT;

  const { selectedCategories, selectedCategoriesCount, selectedOtherItemIds, totalSelectedItemsCount } = useMemo(
    () => getSelectionByCategory(discountedItemCategories, selectedItemIds, selectedCategoryIds),
    [discountedItemCategories, selectedItemIds, selectedCategoryIds],
  );
  const { visibleCategoriesWithItems, visibleOtherItems, hasMore, hasMissingItems } = useMemo(
    () =>
      getVisibleCategoriesWithItems(
        selectedCategories,
        selectedOtherItemIds,
        discountedItems,
        selectedItemIds,
        selectedCategoryIds,
        maxItemsCount,
      ),
    [discountedItems, selectedCategories, selectedOtherItemIds, maxItemsCount, selectedCategoryIds, selectedItemIds],
  );

  if (isLoadingMenuData) {
    return <Loading />;
  }

  return (
    <>
      {visibleCategoriesWithItems.map(category => (
        <Category key={`category-${category.id}`}>
          <CategoryTitle>
            {category.parentCategoryNames.map((name, index) => (
              <Fragment key={`${name}-${index}`}>
                <CategoryName>{name}</CategoryName>
                <CategoryNameCaret />
              </Fragment>
            ))}
            <CategoryName>{`${category.name} (${category.itemIds.length})`}</CategoryName>
          </CategoryTitle>
          <CategoryInfo>
            <InfoIcon />
            {selectedCategoryIds.includes(category.id) ? (
              <FormattedMessage id="campaigns.form.item.category-items-will-be-included" tagName="span" />
            ) : (
              <FormattedMessage
                id="campaigns.form.item.category-items-will-not-be-included"
                values={{
                  b: text => <Emphasized>{text}</Emphasized>,
                }}
                tagName="span"
              />
            )}
          </CategoryInfo>
          <SelectedItemsGrid>
            {category.visibleItems.map(item => !!item && <SelectedDiscountItem key={item.id} item={item} />)}
          </SelectedItemsGrid>
        </Category>
      ))}

      {!!visibleOtherItems.length && (
        <Category>
          {!!visibleCategoriesWithItems.length && ( // Display a title for the rest of items if categories are selected
            <CategoryTitle>
              <CategoryName>{`${formatMessage({ id: 'campaigns.form.item.all-other-items' })} (${selectedOtherItemIds.length})`}</CategoryName>
            </CategoryTitle>
          )}
          <SelectedItemsGrid>
            {visibleOtherItems.map(item => !!item && <SelectedDiscountItem key={item.id} item={item} />)}
          </SelectedItemsGrid>
        </Category>
      )}

      {hasMissingItems && (
        <Alert variant="error">
          <FormattedMessage id="campaigns.form.item.no-item" />
        </Alert>
      )}

      {hasMore && (
        <SelectedDiscountedItemsModal
          discountedItems={discountedItems}
          selectedCategories={selectedCategories}
          selectedOtherItemIds={selectedOtherItemIds}
          selectedCategoriesCount={selectedCategoriesCount}
          totalSelectedItemsCount={totalSelectedItemsCount}
          selectedCategoryIds={selectedCategoryIds}
          onEditClick={onEditClick}
        />
      )}
    </>
  );
};
